import { useParams } from "react-router-dom";
import Header from "./Shared/Header";
import Footer from "./Shared/Footer";
import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from "react-helmet";
import { ThemeContext } from "../Contaxt/ThemeContext";
import axiosIns, { API_URL } from "../config";

const SITE_URL = "https://www.ishivax.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-image.png`;

const stripHtml = (value = "") => (
    value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
);

const sanitizeMeta = (value = "") => (
    typeof value === "string" ? value.replace(/\r?\n/g, " ").replace(/\s+/g, " ").trim() : ""
);

const truncateText = (value = "", limit = 160) => {
    if (value.length <= limit) return value;

    return `${value.slice(0, limit - 3).trim()}...`;
};

const hasText = (value) => typeof value === "string" && value.trim().length > 0;

const buildBlogTitle = (title = "") => {
    const normalizedTitle = title.trim();

    if (!normalizedTitle) return "iShivax Blog | iShivax";
    if (/\|\s*iShivax$/i.test(normalizedTitle)) return normalizedTitle;

    return `${normalizedTitle} | iShivax`;
};

const BlogById = () => {
    const { isLight } = useContext(ThemeContext);
    const [blogData, setBlogData] = useState();
    const params = useParams();
    const blogId = params?.slug; // Assuming the route parameter is named 'slug'

    useEffect(() => {
        const fetchBlogDetailById = async () => {
            try {
                const response = await axiosIns.get(`${API_URL.GetBlogDetailBySlug}?pBlogSlug=${blogId}`);

                if (response.status === 200 && response.data.isSuccess === 1) {
                    setBlogData(response?.data?.data);
                }

            } catch (error) {
                console.error('Failed to fetch blog details:', error);
                return null;
            }
        };
        fetchBlogDetailById();
    }, [blogId])

    const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const day = d.getDate().toString().padStart(2, '0'); // Ensure 2 digits for day
        const month = (d.getMonth() + 1).toString().padStart(2, '0'); // Ensure 2 digits for month (months are 0-indexed)
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const metadata = blogData?.metadata || {};
    const blogName = [metadata.metaTitle, metadata.title, blogData?.metaTitle, blogData?.blogTitle]
        .find(hasText) || "iShivax Blog";
    const pageTitle = buildBlogTitle(blogName);
    const fallbackDescription = truncateText(stripHtml(blogData?.blogDescrption || "Read this iShivax blog article for insights about software development, mobile apps, web platforms, IoT and digital technology."));
    const rawBlogDescription = metadata.metaDescription || metadata.description || blogData?.metaDescription || fallbackDescription;
    const rawBlogKeywords = metadata.metaKeywords || metadata.keywords || blogData?.metaKeywords || [blogData?.blogTypeName, blogData?.blogTitle, "iShivax blog", "software development"].filter(Boolean).join(", ");
    const blogDescription = sanitizeMeta(rawBlogDescription);
    const blogKeywords = sanitizeMeta(rawBlogKeywords);
    const canonicalUrl = metadata.canonicalUrl || blogData?.canonicalUrl || `${SITE_URL}/blog/${blogData?.blogSlug || blogId || ""}`;
    const ogImage = blogData?.blogImage || DEFAULT_OG_IMAGE;
    const blogSchema = blogData ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: blogName,
        description: blogDescription,
        image: ogImage,
        datePublished: blogData.blogDate,
        dateModified: blogData.updatedAt || blogData.blogDate,
        url: canonicalUrl,
        author: {
            "@type": "Organization",
            name: "iShivax"
        },
        publisher: {
            "@type": "Organization",
            name: "iShivax",
            logo: {
                "@type": "ImageObject",
                url: `${SITE_URL}/images/logo.png`
            }
        }
    } : null;

    useEffect(() => {
        try {
            if (pageTitle) document.title = pageTitle;

            const setMeta = (attrName, content, isProperty = false) => {
                if (!content) return;
                const selector = isProperty ? `meta[property="${attrName}"]` : `meta[name="${attrName}"]`;
                let el = document.head.querySelector(selector);
                if (!el) {
                    el = document.createElement('meta');
                    if (isProperty) el.setAttribute('property', attrName);
                    else el.setAttribute('name', attrName);
                    document.head.appendChild(el);
                }
                el.setAttribute('content', content);
            };

            setMeta('description', blogDescription);
            setMeta('keywords', blogKeywords);
            setMeta('og:description', blogDescription, true);
            setMeta('og:title', pageTitle, true);
            setMeta('og:image', ogImage, true);
            setMeta('og:url', canonicalUrl, true);

            let linkCanonical = document.head.querySelector('link[rel="canonical"]');
            if (!linkCanonical) {
                linkCanonical = document.createElement('link');
                linkCanonical.setAttribute('rel', 'canonical');
                document.head.appendChild(linkCanonical);
            }
            linkCanonical.setAttribute('href', canonicalUrl);
        } catch (e) {
            // ignore
        }
    }, [pageTitle, blogDescription, blogKeywords, canonicalUrl, ogImage]);

    return (
        <div>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={blogDescription} />
                <meta name="keywords" content={blogKeywords} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={blogDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="article" />
                <meta property="og:image" content={ogImage} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={blogDescription} />
                <meta name="twitter:image" content={ogImage} />
                {blogSchema && <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>}
            </Helmet>
            <Header />
            <section>
                <div className={`fw-main-row ${isLight ? 'black-bg-content-LIGHT' : 'black-bg-content'}`}>
                    <div className="fw-full-container-top" style={{ backgroundImage: 'url(/images/bg-dots.png)' }}>
                        <div className='fw-container'>
                            <div className="fw-row">
                                <div className="fw-col-xs-12 ">
                                    <div className="fw-divider-space"></div>
                                </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center banner-seo-media-images">
                                <div className='seo-media-images-common'>
                                    <img className="top-image-arrow-1" src="/images/Vector-line-straight.png" alt='' />
                                    <img className="top-image-arrow-2" src="/images/Vector-line-straight.png" alt='' />
                                </div>

                                <div className='seo-media-images-common'>
                                    <img className="top-image-arrow-3" src="/images/Vector-line-straight.png" alt='' />
                                    <img className="top-image-arrow-4" src="/images/Vector-line-straight.png" alt='' />
                                </div>
                            </div>
                            <div className="content-under-image">
                                <div className='top-vector_main'>
                                    <img className="vector-line-common" src="/images/Vector-upper-arrow.png" alt='' />
                                    <div className="fw-row">
                                        <div className="cector-cross-line-allign">
                                            <div className="vector-class-common">
                                                <div className='dots-first'>
                                                    <div className="dot-common"></div>
                                                    <div className="dot-common"></div>
                                                </div>
                                                <img className="vector-cross-common" src="/images/vector.png" alt='' />
                                                <div className='dots-second'>
                                                    <div className="dot-common"></div>
                                                    <div className="dot-common"></div>
                                                </div>
                                            </div>
                                            <div className="fw-heading fw-heading-h2 d-flex justify-content-center align-items-center">
                                                <h2 className={`${isLight ? 'fw-special-title vector-heading-LIGHT' : 'fw-special-title vector-heading'}`}>BLOG</h2>
                                            </div>
                                            <div className="vector-class-common">
                                                <div className='dots-first'>
                                                    <div className="dot-common"></div>
                                                    <div className="dot-common"></div>
                                                </div>
                                                <img className="vector-cross-common" src="/images/vector.png" alt='' />
                                                <div className='dots-second'>
                                                    <div className="dot-common"></div>
                                                    <div className="dot-common"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <img className="vector-line-common1" src="/images/Vector-upper-arrow.png" alt='' />
                                </div>
                                <div className="fw-row">
                                    <div className="fw-col-xs-12 ">
                                        <div className="fw-divider-space" style={{ marginTop: '20px' }}></div>
                                    </div>
                                </div>
                                <div className="fw-row">
                                    <div className="container">
                                        <div className="fw-col-xs-12 ">

                                        </div>
                                        <div className="fw-col-xs-12 ">
                                            <h1 className={`${isLight ? 'heading-common-main-LIGHT' : 'heading-common-main'}`}>
                                                {blogName}
                                            </h1>

                                        </div>
                                        <div className="fw-col-xs-12 ">

                                        </div>
                                    </div>

                                </div>

                                <div className="fw-row about-top-frame">
                                    <div className="container">
                                        {!isLight && (
                                            <div className="fw-col-xs-12 ">
                                                <div className="">
                                                    <img className="services-image-desk"
                                                        src="/images/TECHtechaboutus.webp" alt="" style={{width:"80%"}} />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                </div>
                                <div className="fw-row">
                                    <div className="fw-col-xs-12 ">
                                        <div className="fw-divider-space" style={{ marginTop: '50px' }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="" style={{ position: 'relative', overflow: "hidden" }}>
                <div className="">
                    <div className="our-blogs-section">
                        <div className='container'>
                            <div className='our_blog_container'>
                                {/* <div className='our_blog_heading_container'>
                                    <div className='our_blog_inner'>
                                        <h4 className="blog_inner_heading_blogpage">A Deep Dive into Our Consulting Blogs</h4>
                                        <p className="blog_page_description mb-0">Our agency help businesses develop strategic plans to define their long-term goals, objectives, and the actions needed to achieve them.</p>
                                    </div>
                                </div> */}
                                {/* <div className='our_blog_select_type'>
                                    <div className='our_blog_select_type_inner'>
                                        <div className='our_blog_type_button'>
                                            <button className="deActive"> {blogData?.blogTypeName}</button>
                                        </div>
                                    </div>
                                </div> */}
                                <div className='our_blog_card_container pt-4'>
                                    <div className='our_blog_card_inner row justify-content-center'>
                                        <>
                                            <div className='our_blog_card_image col-lg-10 col-12 hover_inAll mb-4'>
                                                <div className=''>
                                                    <div className='our_blog_card_image_container'>
                                                        <img src={`${blogData?.blogImage}`} alt='blog' />
                                                    </div>
                                                    <div className='our_blog_card_content'>
                                                        <span className='our_blog_date'>{formatDate(blogData?.blogDate)}</span>
                                                        <h4 className='our_blog_title mb-0'>{blogData?.blogTitle}</h4>
                                                        <div
                                                            className="our_blog_description mb-0"
                                                            dangerouslySetInnerHTML={{ __html: blogData?.blogDescrption }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    )
}
export default BlogById;
